import { CONFIG } from '../config'
import { toBase64, fromBase64, randomBytes } from './crypto'

// Check if WebAuthn PRF extension is supported
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable ===
      'function'
  )
}

// Check if platform authenticator is available
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isPasskeySupported()) return false
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

// Generate PRF salt for a vault
export function generatePRFSalt(): Uint8Array {
  return randomBytes(32)
}

// Create a new passkey credential with PRF extension
export async function createPasskeyCredential(
  vaultId: string,
  userEmail: string,
  prfSalt: Uint8Array
): Promise<{
  credentialId: string
  publicKey: string
  prfOutput: Uint8Array
} | null> {
  if (!isPasskeySupported()) return null

  const challenge = randomBytes(32)
  const userId = new TextEncoder().encode(userEmail)

  try {
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: CONFIG.WEBAUTHN_RP_NAME,
          id: CONFIG.WEBAUTHN_RP_ID || undefined,
        },
        user: {
          id: userId,
          name: userEmail,
          displayName: `Vault ${vaultId}`,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'required',
        },
        extensions: {
          prf: {
            eval: {
              first: prfSalt,
            },
          },
        } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null

    if (!credential) return null

    const response = credential.response as AuthenticatorAttestationResponse
    const extensionResults = credential.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } }
    }

    // Check if PRF was evaluated
    if (!extensionResults.prf?.results?.first) {
      console.warn('PRF extension not supported by this authenticator')
      return null
    }

    return {
      credentialId: toBase64(new Uint8Array(credential.rawId)),
      publicKey: toBase64(new Uint8Array(response.getPublicKey() || [])),
      prfOutput: new Uint8Array(extensionResults.prf.results.first),
    }
  } catch (error) {
    console.error('Failed to create passkey:', error)
    return null
  }
}

// Authenticate with existing passkey and get PRF output
export async function authenticateWithPasskey(
  credentialId: string,
  prfSalt: Uint8Array
): Promise<Uint8Array | null> {
  if (!isPasskeySupported()) return null

  const challenge = randomBytes(32)

  try {
    const credential = (await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: CONFIG.WEBAUTHN_RP_ID || undefined,
        allowCredentials: [
          {
            type: 'public-key',
            id: fromBase64(credentialId),
          },
        ],
        userVerification: 'required',
        extensions: {
          prf: {
            eval: {
              first: prfSalt,
            },
          },
        } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null

    if (!credential) return null

    const extensionResults = credential.getClientExtensionResults() as {
      prf?: { results?: { first?: ArrayBuffer } }
    }

    if (!extensionResults.prf?.results?.first) {
      console.warn('PRF extension not supported')
      return null
    }

    return new Uint8Array(extensionResults.prf.results.first)
  } catch (error) {
    console.error('Failed to authenticate with passkey:', error)
    return null
  }
}
